// src/pages/Profile.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import AvatarCropModal from "../components/AvatarCropModal";
import ReCAPTCHA from "react-google-recaptcha";

const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2;

const normalizeInfluencerProfiles = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean)
    .slice(0, 10);
};

const getEditableInfluencerProfiles = (value) => {
  const cleaned = normalizeInfluencerProfiles(value).slice(0, 3);
  while (cleaned.length < 3) cleaned.push("");
  return cleaned;
};

const formatUserType = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "User";
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

const Profile = () => {
  const { currentUser } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordResetSending, setPasswordResetSending] = useState(false);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    tone: "success",
    title: "",
    message: "",
  });

  const [fieldErrors, setFieldErrors] = useState({
    first_name: "",
    last_name: "",
  });
  const [phoneError, setPhoneError] = useState(false);
  const [phoneErrorText, setPhoneErrorText] = useState("");

  const fileInputRef = useRef(null);
  const recaptchaRef = useRef(null);
  const allowedExtensions = ["jpg", "jpeg", "png", "webp"];

  // 📱 detect phone/tablet to match app-wide small-screen breakpoint
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const openActionDialog = ({ tone = "success", title, message }) => {
    setActionDialog({
      open: true,
      tone,
      title: title || (tone === "success" ? "Success" : "Unable to Complete Request"),
      message: message || "",
    });
  };

  const closeActionDialog = () => {
    setActionDialog({
      open: false,
      tone: "success",
      title: "",
      message: "",
    });
  };

  const getFreshToken = async () => {
    if (!SITE_KEY_V2) {
      throw new Error("captcha-not-configured");
    }

    if (!recaptchaRef.current) {
      throw new Error("captcha-not-ready");
    }

    const EXEC_TIMEOUT_MS = 15000;

    const execPromise = recaptchaRef.current.executeAsync();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("captcha-timeout")), EXEC_TIMEOUT_MS)
    );

    try {
      return await Promise.race([execPromise, timeoutPromise]);
    } finally {
      try {
        recaptchaRef.current.reset();
      } catch {}
    }
  };

  const getActionFailureMessage = (err, fallback) => {
    const message = String(err?.message || "");

    if (
      message.includes("captcha") ||
      message.includes("Timeout") ||
      message.includes("timeout")
    ) {
      return "reCAPTCHA expired or failed. Please try again.";
    }

    return message || fallback;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      openActionDialog({
        tone: "error",
        title: "Unsupported Image",
        message: "Only image files (JPG, PNG, WEBP) are allowed.",
      });
      e.target.value = "";
      return;
    }
    setSelectedImageFile(file);
    setShowCropModal(true);
  };

  const handleCropSave = async (imageFile) => {
    if (saving) return;

    setShowCropModal(false);
    setSaving(true);

    try {
      const jpegBlob = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(
              (blob) => {
                if (blob) resolve(blob);
                else reject(new Error("JPEG conversion failed"));
              },
              "image/jpeg",
              0.92
            );
          };
          img.onerror = reject;
          img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });

      // Run the same invisible reCAPTCHA gate before the protected photo update.
      const recaptchaToken = await getFreshToken();

      const storage = getStorage(undefined, "gs://chat-legal-35784-profilepics");
      const extension = "jpg";
      const storageRef = ref(
        storage,
        `profile_pictures/${currentUser.uid}.${extension}`
      );
      await uploadBytes(storageRef, jpegBlob, { contentType: "image/jpeg" });
      const downloadURL = await getDownloadURL(storageRef);

      const idToken = await currentUser.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/update-profile-picture`,
        { photo_url: downloadURL },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "X-Recaptcha-Token": recaptchaToken,
          },
        }
      );

      setProfile((prev) => ({ ...prev, photo_url: downloadURL }));
      openActionDialog({
        tone: "success",
        title: "Profile Photo Updated",
        message: "Your profile photo has been updated successfully.",
      });
    } catch (err) {
      console.error("❌ Upload error:", err);
      openActionDialog({
        tone: "error",
        title: "Photo Upload Failed",
        message: getActionFailureMessage(
          err,
          "We could not update your profile photo. Please try again."
        ),
      });
    } finally {
      setSaving(false);
      setSelectedImageFile(null);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInfluencerProfileChange = (index, value) => {
    setFormData((prev) => {
      const nextProfiles = getEditableInfluencerProfiles(prev.influencer_profiles);
      nextProfiles[index] = value;
      return {
        ...prev,
        influencer_profiles: nextProfiles,
      };
    });
  };

  // ⚖️ Same pattern as Signup: countryCode + phone → E.164 (optional)
  const validateAndFormatPhone = (countryCodeRaw, phoneRaw) => {
    const ccDigits = (countryCodeRaw || "").replace(/\D/g, "");
    const phoneDigitsRaw = (phoneRaw || "").replace(/\D/g, "");
    const hasAnyInput = ccDigits.length > 0 || phoneDigitsRaw.length > 0;

    // Phone is optional: if both empty, accept null
    if (!hasAnyInput) {
      return { phoneE164: null, error: null };
    }

    if (!ccDigits || !phoneDigitsRaw) {
      return {
        phoneE164: null,
        error:
          "Please enter both country code and phone number, or leave them both blank.",
      };
    }

    let phoneDigits = phoneDigitsRaw;

    // Special handling for U.S. numbers
    if (ccDigits === "1") {
      if (phoneDigits.length === 11 && phoneDigits.startsWith("1")) {
        phoneDigits = phoneDigits.slice(1);
      }
      if (phoneDigits.length !== 10) {
        return {
          phoneE164: null,
          error: "Please enter a valid 10-digit U.S. phone number.",
        };
      }
    }

    const phoneE164 = `+${ccDigits}${phoneDigits}`;
    return { phoneE164, error: null };
  };

  const handleSaveChanges = async () => {
    if (profileSaving) return;

    // Reset inline field errors before validating the form.
    setFieldErrors({ first_name: "", last_name: "" });
    setPhoneError(false);
    setPhoneErrorText("");

    // Required fields: First + Last (email is fixed / non-editable)
    const newErrors = {};
    if (!formData.first_name || !formData.first_name.trim()) {
      newErrors.first_name = "First name is required.";
    }
    if (!formData.last_name || !formData.last_name.trim()) {
      newErrors.last_name = "Last name is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    // Phone normalize to E.164
    const { phoneE164, error: phoneValidationError } = validateAndFormatPhone(
      formData.phone_country_code,
      formData.phone_number
    );

    if (phoneValidationError) {
      setPhoneError(true);
      setPhoneErrorText(phoneValidationError);
      return;
    }

    setProfileSaving(true);

    try {
      // Run invisible reCAPTCHA only after ordinary form validation passes.
      const recaptchaToken = await getFreshToken();
      const idToken = await currentUser.getIdToken();

      // Re-pack nested address object for backend / Firestore
      const payload = {
        first_name: formData.first_name || "",
        middle_name: formData.middle_name || "",
        last_name: formData.last_name || "",
        phone: phoneE164 || null,
        language: formData.language || "",
        address: {
          address1: formData.address1 || "",
          address2: formData.address2 || "",
          city: formData.city || "",
          state_province: formData.state_province || "",
          postal_code: formData.postal_code || "",
          country: formData.country || "",
        },
      };

      if (formData.company_name) {
        payload.company_name = formData.company_name;
      }

      if (
        formData.is_influencer ||
        normalizeInfluencerProfiles(formData.influencer_profiles).length > 0
      ) {
        payload.influencer_profiles = normalizeInfluencerProfiles(
          formData.influencer_profiles
        );
      }

      // Keep the reCAPTCHA token out of the Firestore profile payload.
      // The backend can validate it from this header without accidentally
      // persisting the token as a profile field.
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/profile`, payload, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "X-Recaptcha-Token": recaptchaToken,
        },
      });

      const normalizedInfluencerProfiles = normalizeInfluencerProfiles(
        formData.influencer_profiles
      );

      const updatedForm = {
        ...formData,
        phone: phoneE164,
        influencer_profiles: normalizedInfluencerProfiles,
      };

      setProfile((prev) => ({
        ...prev,
        ...updatedForm,
        influencer_profiles: normalizedInfluencerProfiles,
      }));
      setFormData(updatedForm);
      setIsEditing(false);

      openActionDialog({
        tone: "success",
        title: "Profile Updated",
        message: "Your profile changes have been saved successfully.",
      });
    } catch (err) {
      console.error("❌ Save error:", err);
      openActionDialog({
        tone: "error",
        title: "Unable to Save Profile",
        message: getActionFailureMessage(
          err,
          "We could not save your profile updates. Please try again."
        ),
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (passwordResetSending) return;

    const accountEmail = String(currentUser?.email || "").trim().toLowerCase();
    if (!accountEmail) {
      openActionDialog({
        tone: "error",
        title: "Account Email Unavailable",
        message: "Unable to determine your account email.",
      });
      return;
    }

    setPasswordResetSending(true);

    try {
      const recaptchaToken = await getFreshToken();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/auth/request-password-reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: accountEmail,
            email_action_link: `${process.env.REACT_APP_FRONTEND_URL}/login`,
            recaptchaToken,
          }),
        }
      );

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok || data.success === false) {
        throw new Error(
          data.error ||
            data.message ||
            "Failed to send the password reset email."
        );
      }

      openActionDialog({
        tone: "success",
        title: "Reset Link Sent",
        message:
          data.message ||
          "If an account exists for this email, a password reset link has been sent.",
      });
    } catch (err) {
      console.error("❌ Password reset request error:", err);
      openActionDialog({
        tone: "error",
        title: "Unable to Send Reset Link",
        message: getActionFailureMessage(
          err,
          "Failed to send the password reset email. Please try again."
        ),
      });
    } finally {
      setPasswordResetSending(false);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!currentUser) return;
        const idToken = await currentUser.getIdToken();
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/profile`,
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );

        const raw = res.data || {};
        const addr = raw.address || {};

        // Flatten nested + legacy flat fields so UI reads everything correctly
        const flattened = {
          ...raw,
          address1:
            addr.address1 ||
            addr.line1 ||
            addr.address_line1 ||
            raw.address1 ||
            "",
          address2:
            addr.address2 ||
            addr.line2 ||
            addr.address_line2 ||
            raw.address2 ||
            "",
          city: addr.city || raw.city || "",
          state_province:
            addr.state_province ||
            addr.region ||
            addr.state ||
            raw.state_province ||
            "",
          postal_code:
            addr.postal_code || addr.zip || raw.postal_code || "",
          country: addr.country || raw.country || "",
          influencer_profiles: normalizeInfluencerProfiles(
            raw.influencer_profiles
          ),
        };

        // 🔢 Split E.164 phone into country code + local number for the UI
        let phone_country_code = "";
        let phone_number = "";
        const rawPhone = flattened.phone || raw.phone || "";

        if (rawPhone && typeof rawPhone === "string") {
          let phone = rawPhone.trim();
          if (phone.startsWith("+")) phone = phone.slice(1);
          const digits = phone.replace(/\D/g, "");
          if (digits) {
            if (digits.length === 11 && digits.startsWith("1")) {
              phone_country_code = "1";
              phone_number = digits.slice(1);
            } else {
              // generic split: first up to 3 digits as country code, rest as number
              phone_country_code = digits.slice(0, 3);
              phone_number = digits.slice(3);
            }
          }
        }

        if (!phone_country_code) {
          // Default US code for new/blank profiles
          phone_country_code = "1";
        }

        flattened.phone_country_code = phone_country_code;
        flattened.phone_number = phone_number;

        setProfile(flattened);
        setFormData(flattened);
      } catch (err) {
        console.error("❌ Profile fetch error:", err);
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [currentUser]);

  const renderCenteredState = (title, message) => (
    <div style={styles.outerWrap}>
      <div style={styles.centerStateCard}>
        <div style={styles.loadingMark} />
        <div style={styles.centerStateTitle}>{title}</div>
        {!!message && <div style={styles.centerStateText}>{message}</div>}
      </div>
    </div>
  );

  if (loading) return renderCenteredState("Loading profile", "Preparing your account details…");
  if (error) return renderCenteredState("Unable to load profile", error);
  if (!profile) return renderCenteredState("No profile data found", "Please refresh the page or sign in again.");

  const isProfessionalUser =
    profile.user_type === "attorney" ||
    profile.user_type === "law_firm" ||
    profile.user_type === "paralegal_firm";

  const isInfluencer = !!profile.is_influencer;
  const influencerProfiles = normalizeInfluencerProfiles(profile.influencer_profiles);
  const editableInfluencerProfiles = getEditableInfluencerProfiles(
    formData.influencer_profiles
  );
  const hasInfluencerSection = isInfluencer || influencerProfiles.length > 0;

  const fullName = [profile.first_name, profile.middle_name, profile.last_name]
    .filter(Boolean)
    .join(" ") || "User";

  const initials = [profile.first_name, profile.last_name]
    .filter(Boolean)
    .map((value) => String(value).charAt(0).toUpperCase())
    .join("") || "?";

  const pageWrapStyle = {
    ...styles.outerWrap,
    ...(isSmallScreen ? styles.outerWrapCompact : null),
  };

  const shellGridStyle = {
    ...styles.shellGrid,
    ...(isSmallScreen ? styles.shellGridMobile : null),
  };

  const profileHeroStyle = {
    ...styles.profileHero,
    ...(isSmallScreen ? styles.profileHeroMobile : null),
  };

  const profileHeroLeftStyle = {
    ...styles.profileHeroLeft,
    ...(isSmallScreen ? styles.profileHeroLeftMobile : null),
  };

  const actionRowStyle = {
    ...styles.actionRow,
    ...(isSmallScreen ? styles.actionRowMobile : null),
  };

  const unifiedPanelStyle = {
    ...styles.unifiedPanel,
    ...(isSmallScreen ? styles.unifiedPanelMobile : null),
  };

  const detailGridStyle = {
    ...styles.detailGrid,
    ...(isSmallScreen ? styles.detailGridMobile : null),
  };

  const buttonBaseWidth = isSmallScreen ? styles.fullWidthButton : null;

  const onCancelEdit = () => {
    setFormData(profile);
    setFieldErrors({ first_name: "", last_name: "" });
    setPhoneError(false);
    setPhoneErrorText("");
    setIsEditing(false);
  };

  return (
    <div style={pageWrapStyle}>
      <div style={styles.pageHeader}>
        <h1 style={{ ...styles.title, textAlign: isSmallScreen ? "center" : "left" }}>My Profile</h1>
        <p style={{ ...styles.subtitle, textAlign: isSmallScreen ? "center" : "left" }}>
          Manage your identity, contact details, security, and partner profile.
        </p>
      </div>

      <div style={shellGridStyle}>
        <main style={{ minWidth: 0 }}>
          <section style={profileHeroStyle}>
            <div style={profileHeroLeftStyle}>
              <div style={styles.avatarWrap}>
                {profile.photo_url ? (
                  <img src={profile.photo_url} alt="Profile" style={styles.avatar} />
                ) : (
                  <div style={styles.avatarPlaceholder}>{initials}</div>
                )}
              </div>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={styles.name}>{fullName}</div>
              </div>
            </div>

            <div style={actionRowStyle}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />

              {!isEditing ? (
                <>
                  <button type="button" onClick={() => setIsEditing(true)} style={{ ...styles.primaryButton, ...buttonBaseWidth }}>
                    ✎ Edit Profile
                  </button>
                  <button
                    type="button"
                    onClick={handlePasswordReset}
                    disabled={passwordResetSending}
                    style={{
                      ...styles.secondaryButton,
                      ...(passwordResetSending ? styles.btnDisabled : null),
                      ...buttonBaseWidth,
                    }}
                  >
                    {passwordResetSending ? "Sending…" : "🔒 Change Password"}
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={saving}
                    style={{ ...styles.secondaryButton, ...(saving ? styles.btnDisabled : null), ...buttonBaseWidth }}
                    title="Upload a new profile photo"
                  >
                    {saving ? "Uploading…" : "Change Photo"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleSaveChanges}
                    disabled={profileSaving}
                    style={{
                      ...styles.saveButton,
                      ...(profileSaving ? styles.btnDisabled : null),
                      ...buttonBaseWidth,
                    }}
                  >
                    {profileSaving ? "Saving…" : "Save Changes"}
                  </button>
                  <button type="button" onClick={onCancelEdit} style={{ ...styles.ghostButton, ...buttonBaseWidth }}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={saving}
                    style={{ ...styles.secondaryButton, ...(saving ? styles.btnDisabled : null), ...buttonBaseWidth }}
                    title="Upload a new profile photo"
                  >
                    {saving ? "Uploading…" : "Change Photo"}
                  </button>
                </>
              )}
            </div>
          </section>

          <section style={unifiedPanelStyle}>
            <ProfileSection title="Personal Information">
              <div style={detailGridStyle}>
                <DetailField
                  label="First Name *"
                  name="first_name"
                  value={profile.first_name || ""}
                  editValue={formData.first_name || ""}
                  isEditing={isEditing}
                  onChange={handleEditChange}
                  error={fieldErrors.first_name}
                />
                <DetailField
                  label="Middle Name"
                  name="middle_name"
                  value={profile.middle_name || ""}
                  editValue={formData.middle_name || ""}
                  isEditing={isEditing}
                  onChange={handleEditChange}
                />
                <DetailField
                  label="Last Name *"
                  name="last_name"
                  value={profile.last_name || ""}
                  editValue={formData.last_name || ""}
                  isEditing={isEditing}
                  onChange={handleEditChange}
                  error={fieldErrors.last_name}
                />
                <DetailField
                  label="Preferred Language"
                  name="language"
                  value={profile.language || ""}
                  editValue={formData.language || ""}
                  isEditing={isEditing}
                  onChange={handleEditChange}
                />
                {(isProfessionalUser || profile.company_name || formData.company_name) && (
                  <DetailField
                    label="Company Name"
                    name="company_name"
                    value={profile.company_name || ""}
                    editValue={formData.company_name || ""}
                    isEditing={isEditing}
                    onChange={handleEditChange}
                  />
                )}
              </div>
            </ProfileSection>

            <ProfileSection title="Contact Information">
              <div style={detailGridStyle}>
                <ReadOnlyEmail value={profile.email || currentUser?.email || ""} />
                <PhoneField
                  value={profile.phone || ""}
                  countryCode={formData.phone_country_code || ""}
                  phoneNumber={formData.phone_number || ""}
                  isEditing={isEditing}
                  onChange={handleEditChange}
                  hasError={phoneError}
                  errorText={phoneErrorText}
                />
              </div>
            </ProfileSection>

            <ProfileSection title="Address">
              <div style={detailGridStyle}>
                <DetailField label="Street Address" name="address1" value={profile.address1 || ""} editValue={formData.address1 || ""} isEditing={isEditing} onChange={handleEditChange} />
                <DetailField label="Apt / Suite" name="address2" value={profile.address2 || ""} editValue={formData.address2 || ""} isEditing={isEditing} onChange={handleEditChange} />
                <DetailField label="City" name="city" value={profile.city || ""} editValue={formData.city || ""} isEditing={isEditing} onChange={handleEditChange} />
                <DetailField label="State / Province" name="state_province" value={profile.state_province || ""} editValue={formData.state_province || ""} isEditing={isEditing} onChange={handleEditChange} />
                <DetailField label="Postal Code / Zip" name="postal_code" value={profile.postal_code || ""} editValue={formData.postal_code || ""} isEditing={isEditing} onChange={handleEditChange} />
                <DetailField label="Country" name="country" value={profile.country || ""} editValue={formData.country || ""} isEditing={isEditing} onChange={handleEditChange} />
              </div>
            </ProfileSection>

            <ProfileSection title="Security">
              <div style={{ ...styles.securityRow, ...(isSmallScreen ? styles.securityRowMobile : null) }}>
                <div>
                  <div style={styles.securityText}>Password reset links are sent to your account email.</div>
                  <div style={styles.securitySubText}>{profile.email || currentUser?.email || ""}</div>
                </div>
                <button
                  type="button"
                  onClick={handlePasswordReset}
                  disabled={passwordResetSending}
                  style={{
                    ...styles.secondaryButton,
                    ...(passwordResetSending ? styles.btnDisabled : null),
                    ...buttonBaseWidth,
                  }}
                >
                  {passwordResetSending ? "Sending…" : "Send Reset Link"}
                </button>
              </div>
            </ProfileSection>

            {hasInfluencerSection && (
              <ProfileSection title="Partner Profile" last>
                <div style={styles.partnerSummaryRow}>
                  <div style={styles.partnerPair}>
                    <span style={styles.partnerLabel}>Partner application</span>
                    <span style={isInfluencer ? styles.approvedBadge : styles.neutralBadge}>{isInfluencer ? "Approved" : "No"}</span>
                  </div>
                  <div style={styles.partnerPair}>
                    <span style={styles.partnerLabel}>Saved profiles</span>
                    <strong style={styles.partnerCount}>{isEditing ? normalizeInfluencerProfiles(formData.influencer_profiles).length : influencerProfiles.length}</strong>
                  </div>
                </div>

                {isEditing ? (
                  <div style={styles.partnerEditStack}>
                    <input
                      type="text"
                      value={editableInfluencerProfiles[0] || ""}
                      onChange={(e) => handleInfluencerProfileChange(0, e.target.value)}
                      placeholder="Primary social media profile or website"
                      style={styles.input}
                    />
                    <input
                      type="text"
                      value={editableInfluencerProfiles[1] || ""}
                      onChange={(e) => handleInfluencerProfileChange(1, e.target.value)}
                      placeholder="Additional social media profile or website"
                      style={styles.input}
                    />
                    <input
                      type="text"
                      value={editableInfluencerProfiles[2] || ""}
                      onChange={(e) => handleInfluencerProfileChange(2, e.target.value)}
                      placeholder="Additional social media profile or website"
                      style={styles.input}
                    />
                  </div>
                ) : influencerProfiles.length > 0 ? (
                  <div style={styles.partnerLinkList}>
                    {influencerProfiles.map((item, index) => (
                      <div key={`${item}-${index}`} style={styles.partnerLinkRow}>
                        <span style={styles.partnerLinkIndex}>{index + 1}</span>
                        <span style={styles.partnerLinkText}>{item}</span>
                        <span style={styles.externalIcon}>↗</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={styles.noticeText}>No social media profiles or website links were found in your saved profile.</div>
                )}

                <div style={styles.panelFootnote}>ⓘ Partner details are only shown for partner-enabled accounts.</div>
              </ProfileSection>
            )}
          </section>
        </main>

        {!isSmallScreen && (
          <aside style={styles.asidePanel}>
            <div style={styles.noteIcon}>i</div>
            <div style={styles.noteTitle}>Profile note</div>
            <div style={styles.noteText}>
              Keep your information up to date for smoother communication, account security, billing, alerts, and saved documents.
            </div>
            <div style={styles.noteDivider} />
            <div style={styles.asideMetaRow}>
              <span>User type</span>
              <strong>{formatUserType(profile.user_type)}</strong>
            </div>
            <div style={styles.asideMetaRow}>
              <span>Status</span>
              <strong>{hasInfluencerSection ? "Partner" : "Standard"}</strong>
            </div>
          </aside>
        )}
      </div>

      {SITE_KEY_V2 && (
        <div style={{ height: 0, overflow: "hidden" }}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={SITE_KEY_V2}
            size="invisible"
          />
        </div>
      )}

      {showCropModal && selectedImageFile && (
        <AvatarCropModal
          imageFile={selectedImageFile}
          onCancel={() => setShowCropModal(false)}
          onSave={handleCropSave}
        />
      )}

      {actionDialog.open && (
        <div style={styles.dialogOverlay} role="presentation">
          <div
            style={styles.dialogCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-action-dialog-title"
          >
            <div
              style={{
                ...styles.dialogIcon,
                ...(actionDialog.tone === "error"
                  ? styles.dialogIconError
                  : styles.dialogIconSuccess),
              }}
            >
              {actionDialog.tone === "error" ? "!" : "✓"}
            </div>

            <h3 id="profile-action-dialog-title" style={styles.dialogTitle}>
              {actionDialog.title}
            </h3>

            <p style={styles.dialogMessage}>{actionDialog.message}</p>

            <button
              type="button"
              onClick={closeActionDialog}
              style={styles.dialogButton}
              autoFocus
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileSection = ({ title, children, last = false }) => (
  <div style={{ ...styles.profileSection, ...(last ? styles.profileSectionLast : null) }}>
    <div style={styles.sectionTitle}>{title}</div>
    {children}
  </div>
);

const DetailField = ({ label, name, value, editValue, isEditing, onChange, error }) => (
  <div style={styles.detailField}>
    <label style={styles.fieldLabel}>{label}</label>
    {isEditing ? (
      <>
        <input
          type="text"
          name={name}
          value={editValue}
          onChange={onChange}
          style={{
            ...styles.input,
            ...(error ? styles.inputError : null),
          }}
        />
        {!!error && <div style={styles.inlineError}>{error}</div>}
      </>
    ) : (
      <div style={styles.fieldValue}>{value || "—"}</div>
    )}
  </div>
);

const ReadOnlyEmail = ({ value }) => (
  <div style={styles.detailField}>
    <label style={styles.fieldLabel}>Email *</label>
    <div style={styles.readOnlyValue}>
      <span>{value || "—"}</span>
      <span style={styles.lockText}>🔒 read-only</span>
    </div>
  </div>
);

const PhoneField = ({ value, countryCode, phoneNumber, isEditing, onChange, hasError, errorText }) => (
  <div style={styles.detailFieldWide}>
    <label style={styles.fieldLabel}>Phone</label>
    {isEditing ? (
      <>
        <div style={styles.phoneRow}>
          <div style={styles.phoneCountryWrap}>
            <span style={styles.phonePrefix}>+</span>
            <input
              type="text"
              name="phone_country_code"
              value={countryCode}
              onChange={onChange}
              style={styles.phoneCountryInput}
              placeholder="1"
            />
          </div>
          <input
            type="text"
            name="phone_number"
            value={phoneNumber}
            onChange={onChange}
            style={{ ...styles.phoneNumberInput, ...(hasError ? styles.inputError : null) }}
            placeholder="Phone number"
          />
        </div>
        <div style={styles.phoneHint}>US example: Country Code <strong>1</strong>, Phone Number <strong>5551234567</strong></div>
        {hasError && <div style={styles.inlineError}>{errorText || "Please enter a valid phone number."}</div>}
      </>
    ) : (
      <div style={styles.fieldValue}>{value || "—"}</div>
    )}
  </div>
);

const styles = {
  outerWrap: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px 16px 40px",
    boxSizing: "border-box",
    background: "#F5F7FA",
    fontFamily: "sans-serif",
  },
  outerWrapCompact: {
    padding: "14px 12px 34px",
  },
  pageHeader: {
    marginBottom: 16,
  },
  title: {
    margin: 0,
    color: "#0F172A",
    fontSize: 34,
    lineHeight: 1.08,
    fontWeight: 950,
    letterSpacing: "-0.9px",
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    fontWeight: 650,
  },
  shellGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 260px",
    gap: 16,
    alignItems: "stretch",
  },
  shellGridMobile: {
    display: "block",
  },
  profileHero: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 18,
    border: "1px solid rgba(15, 23, 42, 0.10)",
    background: "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 58%, rgba(37,99,235,0.12) 100%)",
    boxShadow: "0 12px 30px rgba(2, 6, 23, 0.06)",
    padding: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 18,
    marginBottom: 14,
  },
  profileHeroMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    padding: 16,
  },
  profileHeroLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    minWidth: 0,
  },
  profileHeroLeftMobile: {
    flexDirection: "column",
    textAlign: "center",
  },
  avatarWrap: {
    width: 104,
    height: 104,
    borderRadius: 999,
    padding: 3,
    background: "linear-gradient(135deg, rgba(30,42,120,0.85), rgba(37,99,235,0.55), rgba(255,255,255,0.95))",
    boxShadow: "0 10px 22px rgba(2, 6, 23, 0.10)",
    boxSizing: "border-box",
    flex: "0 0 auto",
  },
  avatar: {
    width: 98,
    height: 98,
    borderRadius: "50%",
    objectFit: "cover",
    backgroundColor: "#FFFFFF",
    display: "block",
  },
  avatarPlaceholder: {
    width: 98,
    height: 98,
    borderRadius: "50%",
    backgroundColor: "#EEF2FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 32,
    fontWeight: 950,
    color: "#1E2A78",
  },
  name: {
    color: "#0F172A",
    fontSize: 26,
    lineHeight: 1.15,
    fontWeight: 950,
    letterSpacing: "-0.5px",
    overflowWrap: "anywhere",
  },
  email: {
    color: "#334155",
    fontSize: 15,
    marginTop: 6,
    fontWeight: 700,
    overflowWrap: "anywhere",
  },
  badgeRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 10,
    justifyContent: "inherit",
  },
  badgeBlue: {
    background: "rgba(37, 99, 235, 0.10)",
    border: "1px solid rgba(37, 99, 235, 0.16)",
    color: "#1D4ED8",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 12.5,
    fontWeight: 900,
  },
  badgeGreen: {
    background: "rgba(5, 150, 105, 0.10)",
    border: "1px solid rgba(5, 150, 105, 0.16)",
    color: "#047857",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 12.5,
    fontWeight: 900,
  },
  badgeNeutral: {
    background: "rgba(15, 23, 42, 0.06)",
    border: "1px solid rgba(15, 23, 42, 0.10)",
    color: "#334155",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 12.5,
    fontWeight: 900,
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
    flex: "0 0 auto",
  },
  actionRowMobile: {
    width: "100%",
    flexDirection: "column",
    alignItems: "stretch",
  },
  unifiedPanel: {
    borderRadius: 18,
    border: "1px solid rgba(15, 23, 42, 0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2, 6, 23, 0.06)",
    overflow: "hidden",
  },
  unifiedPanelMobile: {
    borderRadius: 16,
  },
  profileSection: {
    padding: "18px 20px",
    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
  },
  profileSectionLast: {
    borderBottom: "none",
  },
  sectionTitle: {
    color: "#1E2A78",
    fontSize: 12.5,
    fontWeight: 950,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: 14,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "14px 22px",
  },
  detailGridMobile: {
    gridTemplateColumns: "1fr",
    gap: 12,
  },
  detailField: {
    minWidth: 0,
  },
  detailFieldWide: {
    minWidth: 0,
    gridColumn: "span 2",
  },
  fieldLabel: {
    display: "block",
    color: "#64748B",
    fontSize: 13,
    fontWeight: 850,
    marginBottom: 7,
  },
  fieldValue: {
    color: "#0F172A",
    fontSize: 15,
    lineHeight: 1.45,
    fontWeight: 500,
    minHeight: 24,
    overflowWrap: "anywhere",
  },
  readOnlyValue: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    flexWrap: "wrap",
    color: "#0F172A",
    fontSize: 15,
    lineHeight: 1.45,
    fontWeight: 700,
    overflowWrap: "anywhere",
  },
  lockText: {
    color: "#64748B",
    fontSize: 12.5,
    fontWeight: 800,
  },
  input: {
    width: "100%",
    padding: "11px 12px",
    fontSize: 15,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,0.16)",
    outline: "none",
    boxSizing: "border-box",
    backgroundColor: "#FFFFFF",
    color: "#0F172A",
    fontWeight: 700,
    boxShadow: "0 8px 18px rgba(2,6,23,0.04)",
  },
  inputError: {
    borderColor: "#B00020",
  },
  inlineError: {
    color: "#B00020",
    fontSize: 12.5,
    marginTop: 6,
    fontWeight: 800,
    lineHeight: 1.4,
  },
  phoneRow: {
    display: "flex",
    gap: 8,
    width: "100%",
  },
  phoneCountryWrap: {
    display: "flex",
    alignItems: "center",
    border: "1px solid rgba(15,23,42,0.16)",
    borderRadius: 12,
    padding: "8px 10px",
    width: 108,
    boxSizing: "border-box",
    boxShadow: "0 8px 18px rgba(2,6,23,0.04)",
    backgroundColor: "#FFFFFF",
  },
  phonePrefix: {
    marginRight: 6,
    fontSize: 14,
    color: "#64748B",
    fontWeight: 900,
  },
  phoneCountryInput: {
    border: "none",
    outline: "none",
    width: "100%",
    fontSize: 14,
    fontWeight: 850,
    color: "#0F172A",
    backgroundColor: "transparent",
  },
  phoneNumberInput: {
    flex: 1,
    minWidth: 0,
    border: "1px solid rgba(15,23,42,0.16)",
    borderRadius: 12,
    padding: "11px 12px",
    fontSize: 15,
    boxSizing: "border-box",
    boxShadow: "0 8px 18px rgba(2,6,23,0.04)",
    backgroundColor: "#FFFFFF",
    fontWeight: 700,
  },
  phoneHint: {
    marginTop: 7,
    fontSize: 12.5,
    color: "#64748B",
    fontWeight: 750,
    lineHeight: 1.45,
  },
  securityRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  securityRowMobile: {
    flexDirection: "column",
    alignItems: "stretch",
  },
  securityText: {
    color: "#334155",
    fontSize: 14.5,
    fontWeight: 750,
    lineHeight: 1.55,
  },
  securitySubText: {
    color: "#64748B",
    fontSize: 13,
    fontWeight: 700,
    marginTop: 4,
    overflowWrap: "anywhere",
  },
  partnerSummaryRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  partnerPair: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    flexWrap: "wrap",
  },
  partnerLabel: {
    color: "#64748B",
    fontSize: 13,
    fontWeight: 850,
  },
  partnerCount: {
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 950,
  },
  approvedBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 10px",
    background: "rgba(5, 150, 105, 0.10)",
    border: "1px solid rgba(5, 150, 105, 0.16)",
    color: "#047857",
    fontSize: 12.5,
    fontWeight: 900,
  },
  neutralBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 10px",
    background: "rgba(15, 23, 42, 0.06)",
    border: "1px solid rgba(15, 23, 42, 0.10)",
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 900,
  },
  partnerLinkList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  partnerLinkRow: {
    display: "grid",
    gridTemplateColumns: "30px minmax(0, 1fr) 24px",
    alignItems: "center",
    gap: 10,
    borderRadius: 12,
    border: "1px solid rgba(15, 23, 42, 0.10)",
    background: "#F8FAFC",
    padding: "9px 10px",
  },
  partnerLinkIndex: {
    width: 24,
    height: 24,
    borderRadius: 8,
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748B",
    fontSize: 12,
    fontWeight: 900,
  },
  partnerLinkText: {
    color: "#1D4ED8",
    fontSize: 14,
    fontWeight: 850,
    overflowWrap: "anywhere",
  },
  externalIcon: {
    color: "#2563EB",
    fontSize: 16,
    fontWeight: 900,
    textAlign: "center",
  },
  partnerEditStack: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  panelFootnote: {
    marginTop: 12,
    color: "#64748B",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.45,
  },
  noticeText: {
    color: "#64748B",
    fontSize: 13.5,
    fontWeight: 750,
    lineHeight: 1.5,
    padding: 12,
    borderRadius: 12,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#F8FAFC",
  },
  asidePanel: {
    borderRadius: 18,
    border: "1px solid rgba(15, 23, 42, 0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2, 6, 23, 0.04)",
    padding: 18,
    alignSelf: "stretch",
    minHeight: 360,
  },
  noteIcon: {
    width: 28,
    height: 28,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(30, 42, 120, 0.08)",
    border: "1px solid rgba(30, 42, 120, 0.14)",
    color: "#1E2A78",
    fontSize: 14,
    fontWeight: 950,
    marginBottom: 12,
  },
  noteTitle: {
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 950,
    marginBottom: 10,
  },
  noteText: {
    color: "#475569",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.65,
  },
  noteDivider: {
    height: 1,
    background: "rgba(15, 23, 42, 0.08)",
    margin: "18px 0",
  },
  asideMetaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 0",
    color: "#64748B",
    fontSize: 13,
    fontWeight: 850,
  },
  primaryButton: {
    appearance: "none",
    border: "1px solid rgba(30,42,120,0.22)",
    background: "#1E2A78",
    color: "#FFFFFF",
    padding: "11px 16px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 13.5,
    boxShadow: "0 10px 22px rgba(30, 42, 120, 0.18)",
    maxWidth: "100%",
    minHeight: 42,
  },
  secondaryButton: {
    appearance: "none",
    border: "1px solid rgba(15,23,42,0.14)",
    background: "#FFFFFF",
    color: "#0F172A",
    padding: "11px 16px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 13.5,
    boxShadow: "0 10px 22px rgba(2, 6, 23, 0.05)",
    maxWidth: "100%",
    minHeight: 42,
  },
  ghostButton: {
    appearance: "none",
    border: "1px solid rgba(15,23,42,0.12)",
    background: "rgba(15,23,42,0.03)",
    color: "#0F172A",
    padding: "11px 16px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 13.5,
    maxWidth: "100%",
    minHeight: 42,
  },
  saveButton: {
    appearance: "none",
    border: "1px solid rgba(16,185,129,0.25)",
    background: "#10B981",
    color: "#FFFFFF",
    padding: "11px 16px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 13.5,
    boxShadow: "0 10px 22px rgba(16, 185, 129, 0.18)",
    maxWidth: "100%",
    minHeight: 42,
  },
  fullWidthButton: {
    width: "100%",
  },
  btnDisabled: {
    opacity: 0.65,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  dialogOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    boxSizing: "border-box",
  },
  dialogCard: {
    width: "100%",
    maxWidth: 420,
    padding: "28px 24px 24px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 14,
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.22)",
    textAlign: "center",
    boxSizing: "border-box",
  },
  dialogIcon: {
    width: 46,
    height: 46,
    margin: "0 auto 14px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    fontWeight: 900,
  },
  dialogIconSuccess: {
    backgroundColor: "#ECFDF3",
    color: "#0A7C2F",
  },
  dialogIconError: {
    backgroundColor: "#FEF2F2",
    color: "#B42318",
  },
  dialogTitle: {
    margin: "0 0 10px",
    color: "#111827",
    fontSize: 22,
    fontWeight: 800,
  },
  dialogMessage: {
    margin: "0 0 22px",
    color: "#4B5563",
    fontSize: 15,
    lineHeight: 1.55,
    overflowWrap: "anywhere",
  },
  dialogButton: {
    minWidth: 120,
    padding: "11px 20px",
    fontSize: 15,
    backgroundColor: "#1E2A78",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
  centerStateCard: {
    minHeight: 320,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2, 6, 23, 0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    textAlign: "center",
    padding: 24,
  },
  loadingMark: {
    width: 34,
    height: 34,
    borderRadius: 999,
    border: "3px solid rgba(30,42,120,0.16)",
    borderTopColor: "#1E2A78",
    marginBottom: 14,
  },
  centerStateTitle: {
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
  },
  centerStateText: {
    marginTop: 6,
    color: "#64748B",
    fontSize: 14,
    fontWeight: 700,
  },
};

export default Profile;
